import React, { useContext, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/th';

import IconButton from '@mui/material/IconButton';
import Badge from '@mui/material/Badge';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import NotificationsIcon from '@mui/icons-material/Notifications';

import { NotificationContext, PRIORITY_CONFIG, notificationPath } from '../context/NotificationContext';
import DesktopNotificationPrompt from './DesktopNotificationPrompt';

dayjs.extend(relativeTime);
dayjs.locale('th');


const KEYWORD_COLORS = [
  { word: 'สร้างใบเสนอราคา', color: '#1976d2' },
  { word: 'บันทึก Draft',    color: '#7b1fa2' },
  { word: 'แก้ไข Draft',     color: '#e65100' },
  { word: 'ลบ Draft',        color: '#c62828' },
  { word: 'กู้คืน Draft',    color: '#00695c' },
];

function renderMessage(message) {
  if (!message) return message;

  const matches = [];
  for (const { word, color } of KEYWORD_COLORS) {
    const idx = message.indexOf(word);
    if (idx !== -1) matches.push({ idx, word, color });
  }
  if (!matches.length) return message;

  matches.sort((a, b) => a.idx - b.idx);

  const parts = [];
  let cursor = 0;
  for (const { idx, word, color } of matches) {
    if (idx < cursor) continue;
    if (idx > cursor) parts.push(message.slice(cursor, idx));
    parts.push(<span key={idx} style={{ color }}>{word}</span>);
    cursor = idx + word.length;
  }
  if (cursor < message.length) parts.push(message.slice(cursor));

  return <>{parts}</>;
}

export default function NotificationBell() {
  const { notifications, unreadCount, markRead, markAllRead } = useContext(NotificationContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();

  const handleOpen = (e) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  const handleItemClick = async (notif) => {
    if (!notif.is_read) await markRead(notif.id);
    handleClose();
    const path = notificationPath(notif.type, notif.cart_guid, notif.ref_doc_no);
    if (path) navigate(path);
  };

  return (
    <>
      <Tooltip title="การแจ้งเตือน">
        <IconButton
          color="inherit"
          onClick={handleOpen}
          sx={{
            width: 36,
            height: 36,
            bgcolor: 'rgba(255,255,255,0.10)',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.20)' },
          }}
        >
          <Badge badgeContent={unreadCount || 0} color="error" invisible={unreadCount === 0}>
            <NotificationsIcon sx={{ fontSize: 20 }} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{
          elevation: 4,
          sx: {
            mt: 1,
            width: 360,
            maxHeight: 480,
            borderRadius: 2.5,
            border: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ px: 2, py: 1.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            การแจ้งเตือน
          </Typography>
          {unreadCount > 0 && (
            <Button size="small" onClick={markAllRead} sx={{ fontSize: '0.75rem', textTransform: 'none' }}>
              อ่านทั้งหมด
            </Button>
          )}
        </Box>
        <Divider />
        <DesktopNotificationPrompt compact />

        {/* List */}
        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {notifications.length === 0 ? (
            <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">ไม่มีการแจ้งเตือน</Typography>
            </Box>
          ) : (
            notifications.map(notif => (
              <Box
                key={notif.id}
                onClick={() => handleItemClick(notif)}
                sx={{
                  px: 2,
                  py: 1.25,
                  cursor: 'pointer',
                  position: 'relative',
                  bgcolor: notif.is_read ? 'transparent' : '#e3f2fd',
                  borderLeft: notif.is_read ? '3px solid transparent' : '4px solid #1565c0',
                  '&:hover': { bgcolor: notif.is_read ? 'action.hover' : '#c8e0f4' },
                  transition: 'background-color 0.15s',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight={notif.is_read ? 400 : 700} sx={{ lineHeight: 1.4 }}>
                    {renderMessage(notif.message)}
                  </Typography>
                  {notif.type !== 'DRAFT_DELETED' && notif.draft_priority && PRIORITY_CONFIG[notif.draft_priority] && (() => {
                    const p = PRIORITY_CONFIG[notif.draft_priority];
                    return (
                      <Chip
                        size="small"
                        label={`${p.emoji} ${p.label}`}
                        sx={{ fontSize: 11, height: 20, bgcolor: p.bg, color: p.color, border: `1px solid ${p.color}40`, fontWeight: 600, px: 0.25 }}
                      />
                    );
                  })()}
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25, display: 'block' }}>
                  {dayjs(notif.created_at).fromNow()}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      </Popover>
    </>
  );
}
